import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, debounceTime } from 'rxjs';

/**
 * Barre de filtres commune aux écrans d'administration.
 *
 * <p>Reprend le principe des facettes de la banque de questions : un en-tête qui
 * annonce combien de filtres sont actifs et permet de tout réinitialiser, une
 * recherche libre débouncée, puis une grille de facettes fournie par l'appelant
 * (typiquement des {@link SearchableSelectComponent} avec une entrée « tout »).</p>
 *
 * <p>Purement présentationnel : il ne connaît ni les données ni les services.
 * Les facettes restent projetées pour que chaque écran garde les siennes, tout
 * en partageant le même cadre visuel et le même comportement.</p>
 */
@Component({
  selector: 'app-filter-bar',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './filter-bar.component.html',
  styleUrl: './filter-bar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterBarComponent {
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Dernier texte émis vers le parent. Sert à distinguer « le parent a changé
   * le texte » (réinitialisation → on resynchronise le champ) de « le parent
   * nous renvoie ce qu'on vient d'émettre » (frappe en cours → on ne touche à
   * rien, sinon le curseur saute).
   */
  private lastEmittedText = '';

  @Input()
  set search(value: string) {
    const incoming = value ?? '';
    if (incoming !== this.lastEmittedText) {
      this.lastEmittedText = incoming;
      this.textDraft.set(incoming);
    }
  }

  /** Nombre de facettes actives : pilote le compteur et le bouton de remise à zéro. */
  readonly activeCount = input(0);

  /** Masque la recherche libre pour les écrans qui n'en ont pas besoin. */
  readonly searchable = input(true);

  readonly titleKey = input('FILTERS.TITLE');
  readonly searchLabelKey = input('FILTERS.SEARCH');
  readonly searchPlaceholderKey = input('FILTERS.SEARCH_PLACEHOLDER');

  /** Lignes visibles / total, affiché quand un filtre est actif. `null` = masqué. */
  readonly resultCount = input<number | null>(null);
  readonly totalCount = input<number | null>(null);

  @Output() searchChange = new EventEmitter<string>();
  @Output() reset = new EventEmitter<void>();

  /** Texte tapé, avant débounce. */
  readonly textDraft = signal('');

  private readonly textInput$ = new Subject<string>();

  readonly hasActiveFilters = computed(() => this.activeCount() > 0);

  readonly showCounts = computed(
    () => this.resultCount() !== null && this.totalCount() !== null,
  );

  constructor() {
    // Sans débounce, chaque frappe relancerait le filtrage (et, pour les écrans
    // qui interrogent le serveur, une requête par caractère).
    this.textInput$
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((text) => {
        // Une frappe encore en attente au moment d'une remise à zéro (ou d'un
        // texte imposé par le parent) est périmée : l'émettre rétablirait un
        // filtre que le champ, vidé, ne montre plus.
        if (text !== this.textDraft()) return;
        this.lastEmittedText = text;
        this.searchChange.emit(text);
      });
  }

  onText(raw: string): void {
    this.textDraft.set(raw);
    this.textInput$.next(raw);
  }

  onReset(): void {
    this.lastEmittedText = '';
    this.textDraft.set('');
    this.reset.emit();
  }
}
